<template>
  <div :class="['theme__popup', { show: showDialog }]" v-click-outside="forceClose">
    <div style="display: flex; flex-direction: row; align-self: flex-start">
      <div style="display: flex; width: 6rem">
        <button
          type="button"
          class="open-btn"
          @click="onClickOpen"
          style="font-size: 0.8rem; text-align: left; display: flex"
        >
          {{ allSegments.length }}
          <VisibleText>&nbsp;segments</VisibleText>
        </button>
      </div>
      <div
        style="
          display: flex;
          max-width: 90%;
          overflow-x: auto;
          overflow-y: hidden;
          padding-bottom: 0.3rem;
        "
        class="light-scroll-bar"
      >
        <div
          class="segment-chip keywords-input-badge pill ui-adaptable"
          v-for="seg in previewSegments"
          :key="seg.question_id + seg.value"
          :class="{
            positive: seg.difference > 0,
            negative: seg.difference < 0,
          }"
        >
          <span class="chip-field">{{ seg.field }}</span>
          <span class="chip-value">{{ seg.value }}</span>

          <span class="arrow">
            {{ seg.difference > 0 ? '↑' : seg.difference < 0 ? '↓' : '' }}
          </span>
        </div>
      </div>
    </div>

    <DialogPopup
      class="theme__popup-dialog centredMargin"
      :class="offsetLogic"
      header-tag="h4"
      :show="showDialog"
      @close="showDialog = false"
      style="margin-top: auto; margin-bottom: auto"
      v-if="showDialog"
    >
      <template #header>
        <div class="segment-controls">
          <div>
            Segments as outliers for the theme:
            <span style="margin-left: 0.5rem">{{ theme_name }} </span> -
            <span>{{ percentage }} %</span>
          </div>
        </div>
      </template>
      <template #body>
        <div class="segment-description">
          <p class="segment-line">
            Segments reveal which groups respond differently. Sort the table to surface the most
            interesting patterns.
          </p>
          <!-- <p class="segment-line">
            The score boosts segments that are both <strong>large</strong> and
            <strong>far from the average</strong>, helping you spot meaningful outliers quickly.
          </p> -->
          <div class="segment-description-grid">
            <!-- Row 1: Diff + Score -->
            <div class="desc-card">
              <span class="desc-badge">Diff</span>
              <p>
                <strong>Difference:</strong> How far this group's response deviates from the average
                baseline.
              </p>
              <p v-if="filteredAndSortedSegments.length > 0" class="desc-example-text">
                For example, for the first outlier below (<strong>{{
                  filteredAndSortedSegments[0].field
                }}</strong
                >: <em>"{{ filteredAndSortedSegments[0].value }}"</em>), its coverage of
                <strong>{{ (filteredAndSortedSegments[0].segment_pct * 100).toFixed(1) }}%</strong>
                represents a difference of
                <strong
                  >{{ (filteredAndSortedSegments[0].difference * 100).toFixed(2) }} points</strong
                >
                from the baseline average of {{ percentage }}%.
              </p>
            </div>

            <div class="desc-card highlight-card">
              <span class="desc-badge score-badge">Score</span>
              <p>
                <strong>Outlier Score:</strong> Prioritizes segments that are both
                <strong>large</strong> and <strong>far from average</strong> to surface meaningful
                insights first.
              </p>
            </div>

            <!-- Row 2: P-Value + Confidence -->
            <div class="desc-card">
              <span class="desc-badge">P‑Value</span>
              <p>
                <strong>Statistical Significance:</strong> Tells you whether the difference is
                likely due to chance. A <strong>low p‑value</strong> (below 0.05) means the segment
                behaves meaningfully differently from the overall group.
              </p>
              <p v-if="filteredAndSortedSegments.length > 0" class="desc-example-text">
                For example, the first segment below has a p‑value of
                <strong>{{ filteredAndSortedSegments[0].p_value.toFixed(3) }}</strong
                >, meaning
                <em>{{
                  filteredAndSortedSegments[0].p_value < 0.05
                    ? 'statistically significant'
                    : 'not statistically significant'
                }}</em
                >.
              </p>
            </div>

            <div class="desc-card">
              <span class="desc-badge">Confidence</span>
              <p>
                <strong>Confidence Interval:</strong> Shows the range where the true value likely
                falls. <strong>Narrow ranges</strong> mean stable estimates;
                <strong>wider ranges</strong> mean more uncertainty.
              </p>
              <p v-if="filteredAndSortedSegments.length > 0" class="desc-example-text">
                For example, the first segment’s confidence interval is
                <strong
                  >{{ (filteredAndSortedSegments[0].ci_low * 100).toFixed(1) }}% –
                  {{ (filteredAndSortedSegments[0].ci_high * 100).toFixed(1) }}%</strong
                >, meaning the true value likely lies somewhere in that range.
              </p>
            </div>
          </div>
        </div>

        <div style="display: flex; flex-direction: row; margin: 1rem">
          <div style="display: flex; flex-direction: column">
            <h2 style="font-size: 1rem">Filter by categorical question</h2>
            <select v-model="filterField">
              <option value="">All questions</option>
              <option v-for="f in uniqueFields" :key="f" :value="f">{{ f }}</option>
            </select>
          </div>

          <button
            class="copy-table-btn"
            @click="copyTable"
            style="height: 1.5rem; margin: auto 2rem; margin-top: 2rem"
          >
            <SvgIconMeaningful icon="copy" />
            <VisibleText>Copy table</VisibleText>
          </button>

          <div v-if="toastMessage" class="local-toast">
            {{ toastMessage }}
          </div>

          <!-- Segmentation Quality Nudge -->
          <div class="segmentation-nudge" style="display: flex; flex-direction: row">
            <div
              class="nudge-icon"
              :class="{ acknowledged: nudgeAcknowledged }"
              @click="nudgeOpen = !nudgeOpen"
            >
              <span v-if="!nudgeAcknowledged">✖</span>
              <span v-else>✔</span>
            </div>

            <transition name="fade">
              <div v-if="nudgeOpen" class="nudge-panel">
                <p class="nudge-text">
                  Segmentation analysis works best when your dataset includes
                  <strong>good quality</strong>, <strong>representative</strong> responses. Before
                  drawing conclusions, take a moment to consider whether key groups were
                  <strong>included</strong> and <strong>fairly represented</strong>.
                </p>

                <label class="nudge-check">
                  <input type="checkbox" v-model="nudgeAcknowledged" />
                  I understand and want to continue.
                </label>
              </div>
            </transition>
          </div>
        </div>

        <table class="segment-table">
          <thead>
            <tr>
              <th @click="sortBy('field')">Field</th>
              <th @click="sortBy('value')">Value</th>
              <th @click="sortBy('segment_size')">Size</th>
              <th @click="sortBy('segment_pct')">Coverage</th>
              <!-- Added lightweight native tooltips via the title attribute -->
              <th
                @click="sortBy('difference')"
                title="Difference: How much this group's response deviates from the average"
              >
                Diff
              </th>
              <!-- <th @click="sortBy('abs_diff')" title="Absolute Difference: The variance from the average regardless of positive/negative direction">Abs Diff</th> -->
              <th
                @click="sortBy('abs_score')"
                title="Score: A metric prioritizing segments that are both large and far from average"
              >
                Scores
              </th>
              <th @click="sortBy('p_value')">p-value</th>
              <th @click="sortBy('confidence_interval')">Confidence</th>

              <th>Indicators</th>
              <th>Favourite</th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="seg in filteredAndSortedSegments"
              :key="seg.question_id + seg.value"
              :class="{
                positive: seg.difference > 0,
                negative: seg.difference < 0,
              }"
            >
              <td class="field-cell">{{ seg.field }}</td>
              <td class="value-cell">{{ seg.value }}</td>
              <td>{{ seg.segment_size }}</td>
              <td>{{ (seg.segment_pct * 100).toFixed(1) }}%</td>
              <td>{{ (seg.difference * 100).toFixed(2) }}</td>
              <!-- <td>{{ Math.abs(seg.difference * 100).toFixed(2) }}</td> -->
              <td>{{ Math.abs(seg.composite_score).toFixed(2) }}</td>
              <td>{{ seg.p_value.toFixed(2) }}</td>
              <td class="ci-cell">
                <span class="ci-range">
                  <span class="ci-low">{{ (seg.ci_low * 100).toFixed(0) }}%</span>
                  <span class="ci-separator">–</span>
                  <span class="ci-high">{{ (seg.ci_high * 100).toFixed(0) }}%</span>
                </span>
              </td>

              <td class="indicators-cell">
                <span v-if="seg.p_value < 0.05" class="badge badge-significant">Significant</span>

                <span v-if="seg.sample_warning === 'low_sample'" class="badge badge-low-sample"
                  >Low sample</span
                >
                <span
                  v-if="seg.sample_warning === 'very_low_sample'"
                  class="badge badge-very-low-sample"
                  >Very low</span
                >

                <span v-if="seg.ci_high - seg.ci_low < 0.1" class="badge badge-high-confidence"
                  >High confidence</span
                >

                <span v-if="seg.p_value >= 0.1" class="badge badge-not-significant"
                  >Not significant</span
                >
              </td>

              <td>
                <span class="star" :class="{ active: isStarred(seg) }" @click="toggleStar(seg)">
                  <SvgIconMeaningful icon="star" title="favourited" />
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </template>
    </DialogPopup>
  </div>
</template>

<script>
import DialogPopup from '@/components/UI/DialogPopup.vue';
import SvgIconMeaningful from '@/components/UI/Svg/SvgIconMeaningful.vue';

export default {
  name: 'SegmentsPopup',
  components: {
    DialogPopup,
    SvgIconMeaningful,
  },
  props: {
    allSegments: {
      default: () => [],
      type: Array,
    },
    starredSegments: {
      default: () => [],
      type: Array,
    },
    theme: {
      default: () => {},
      type: Object,
    },
    hasFilter: {
      default: () => false,
      type: Boolean,
    },
    sortedResponsesLength: {
      default: () => 0,
      type: Number,
    },
    hasCharts: {
      default: () => false,
      type: Boolean,
    },
    hasSentiments: {
      default: () => false,
      type: Boolean,
    },
  },
  data() {
    return {
      showDialog: false,
      sortKey: 'abs_score',
      sortDir: 'desc',
      filterField: '',
      filterDirection: '',
      minSize: 0,
      starred: {},
      toastMessage: '',
      nudgeOpen: false,
      nudgeAcknowledged: false,
    };
  },
  created() {
    this.findStarred();
  },
  computed: {
    uniqueFields() {
      return [...new Set(this.allSegments.map((s) => s.field))];
    },
    sortedSegments() {
      return [...this.allSegments].sort((a, b) => {
        const dir = this.sortDir === 'asc' ? 1 : -1;

        // Sort by absolute difference
        if (this.sortKey === 'abs_diff') {
          return Math.abs(a.difference) > Math.abs(b.difference) ? dir : -dir;
        }

        // Sort by absolute composite score
        if (this.sortKey === 'abs_score') {
          return Math.abs(a.composite_score) > Math.abs(b.composite_score) ? dir : -dir;
        }

        // Sort by confidence interval width
        if (this.sortKey === 'confidence_interval') {
          const widthA = a.ci_high - a.ci_low;
          const widthB = b.ci_high - b.ci_low;
          return widthA > widthB ? dir : -dir;
        }

        // Default sort
        return a[this.sortKey] > b[this.sortKey] ? dir : -dir;
      });
    },

    filteredAndSortedSegments() {
      // Note most of these filters are not useable/active at the moment
      return this.sortedSegments.filter((seg) => {
        if (this.filterField && seg.field !== this.filterField) return false;
        if (this.filterDirection === 'positive' && seg.difference <= 0) return false;
        if (this.filterDirection === 'negative' && seg.difference >= 0) return false;
        if (this.minSize && seg.segment_size < this.minSize) return false;
        return true;
      });
    },

    previewSegments() {
      const starredSegs = this.sortedSegments.filter((seg) => this.isStarred(seg));
      return starredSegs.length > 0 ? starredSegs : this.sortedSegments;
    },
    theme_id() {
      if (this.theme['_id']['$oid']) {
        return this.theme['_id']['$oid'];
      }
      return this.theme['_id'];
    },
    theme_name() {
      return this.theme['name'];
    },
    coverage() {
      if (this.hasFilter) {
        return this.theme.filtered_coverage.total;
      }
      return this.theme.coverage.total;
    },
    percentage() {
      let percentage = 0;
      percentage = (this.coverage / this.sortedResponsesLength) * 100;
      return percentage.toFixed(1);
    },
    offsetLogic() {
      if (this.hasSentiments && this.hasCharts) {
        return 'offsetForChartsAndSentiment';
      }
      if (!this.hasSentiments && this.hasCharts) {
        return 'offsetForCharts';
      }
      if (this.hasSentiments && !this.hasCharts) {
        return 'offsetForSentiment';
      }
      return '';
    },
  },
  methods: {
    forceClose() {
      if (this.showDialog) {
        this.showDialog = false;
      }
    },
    onClickOpen() {
      this.showDialog = !this.showDialog;
    },
    sortBy(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortKey = key;
        this.sortDir = 'desc';
      }
    },
    toggleStar(seg) {
      const key = seg.question_id + seg.value;
      const newState = !this.starred[key];

      this.starred[key] = newState;

      this.$emit('starSegment', {
        key,
        starred: newState,
        theme_id: this.theme_id,
      });
    },

    isStarred(seg) {
      return !!this.starred[seg.question_id + seg.value];
    },

    findStarred() {
      // Clear local state first
      this.starred = {};

      // Convert parent list → local dictionary
      this.starredSegments.forEach((seg) => {
        const key = seg.key;
        this.starred[key] = true;
      });
    },

    copyTable() {
      // Build header row
      const headers = [
        'Field',
        'Value',
        'Size',
        'Coverage',
        'Diff',
        'Abs Diff',
        'Score',
        'P-Value',
        'CI',
        'Starred',
      ];

      // Build rows
      const rows = this.filteredAndSortedSegments.map((seg) => {
        const ciLow = (seg.ci_low * 100).toFixed(1) + '%';
        const ciHigh = (seg.ci_high * 100).toFixed(1) + '%';
        const ciDisplay = `${ciLow} - ${ciHigh}`;

        return [
          seg.field,
          seg.value,
          seg.segment_size,
          (seg.segment_pct * 100).toFixed(1) + '%',
          seg.difference.toFixed(3),
          Math.abs(seg.difference).toFixed(3),
          Math.abs(seg.composite_score).toFixed(2),
          seg.p_value.toFixed(3),
          ciDisplay,
          this.isStarred(seg) ? '★' : '',
        ].join('\t');
      });

      const tsv = [headers.join('\t'), ...rows].join('\n');

      // Copy to clipboard
      navigator.clipboard.writeText(tsv).then(() => {
        this.toastMessage = 'Copied table to clipboard';

        setTimeout(() => {
          this.toastMessage = '';
        }, 2000);
      });
    },
  },
  watch: {
    allSegments: function (val) {
      if (val) {
      }
    },
    showDialog: function (val) {
      if (!val) {
      }
    },
    starredSegments: {
      handler() {
        this.findStarred();
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.theme__popup-dialog {
  max-width: 60vw;
  width: calc(100% * 3);
  /* left: 10%; */
}
.centredMargin {
  left: -25%;
  top: -10rem !important;
}
.offsetForCharts {
  left: -90%;
}
.offsetForChartsAndSentiment {
  left: -120%;
}
.offsetForSentiment {
  left: -70%;
}
.segment-chip {
  display: flex;
  align-items: center;
  font-size: 0.65rem;
  max-width: 12rem; /* compact chip */
  min-width: 8rem; /* prevents collapse to zero */
  overflow: hidden;
  white-space: nowrap;
  border-radius: 0.8em;
  padding: 0.25em 1em;
  margin: 0.25em;
}

.chip-field {
  flex: 2 1 0; /* ~70% of space */
  min-width: 0; /* required for ellipsis */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: 0.25rem;
  font-weight: 600;
  opacity: 0.85;
}

.chip-value {
  flex: 1 1 0; /* ~30% of space */
  min-width: 0; /* required for ellipsis */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: 0.25rem;
  opacity: 0.75;
}

.arrow {
  margin-left: 0.2rem;
  font-weight: bold;
  flex-shrink: 0; /* arrow never ellipsizes */
}

.segment-chip.positive {
  background: #e6f7e6;
  border-color: #8cd98c;
  color: #2d7a2d;
}

.segment-chip.negative {
  background: #fdeaea;
  border-color: #f5a3a3;
  color: #a33a3a;
}

.segment-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
}

.segment-table th {
  cursor: pointer;
  padding: 0.4rem;
  border-bottom: 1px solid #ccc;
}

.segment-table td {
  padding: 0.4rem;
  border-bottom: 1px solid #eee;
}

.segment-table tr.positive {
  background: #f6fff6;
}

.segment-table tr.negative {
  background: #fff6f6;
}

.segment-table td.field-cell,
.segment-table td.value-cell {
  max-width: 12rem; /* adjust as needed */
  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.star {
  cursor: pointer;
  font-size: 1rem;
  color: #bbb; /* grey when NOT starred */
  transition:
    color 0.2s,
    opacity 0.2s;
  opacity: 0.5;
}

.star:hover {
  opacity: 0.8;
}

.star.active {
  color: gold; /* gold when starred */
  opacity: 1;
}

.segment-description {
  margin: 1rem 0;
  padding: 1rem 1.25rem;
  background: linear-gradient(180deg, #fcfcfc, #f7f7f7);
  border-radius: 0.6rem;
  border: 1px solid #e5e5e5;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  font-size: 0.9rem;
  line-height: 1.45rem;
  color: #3a3a3a;
}

.segment-description strong {
  color: #1a1a1a;
  font-weight: 600;
}

.segment-line {
  margin-bottom: 0.5rem;
  margin-top: 0;
}

.segment-line:last-child {
  margin-bottom: 0;
}

.segment-description-grid {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 1rem; /* Replaces the heavy 2rem margins to save horizontal space */
  margin: 1rem 1.5rem;
  font-size: 0.9rem;
  line-height: 1.4;
  color: #4a5568;
}

.desc-card {
  background: #f7fafc;
  border-radius: 6px;
  padding: 1rem;
  border: 1px solid #e2e8f0;

  /* Use a smaller base width so they have room to sit side-by-side */
  flex: 1 1 200px;
  box-sizing: border-box;

  /* Crucial properties to force internal text to break and wrap */
  min-width: 0;
  word-break: break-word;
  overflow-wrap: break-word;
}

/* Ensure the paragraph inside respects the boundaries */
.desc-card p {
  margin: 0;
  white-space: normal; /* Overrides any inheritance forcing 'nowrap' */
}
.desc-badge {
  display: inline-block;
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: #edf2f7;
  color: #4a5568;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  margin-bottom: 0.5rem;
}

/* Subtle accent for the Score card since it's the primary sorting metric */
.highlight-card {
  background: #f0fdf4;
  border-color: #bbf7d0;
}

.score-badge {
  background: #dcfce7;
  color: #15803d;
}

.desc-example-text {
  margin-top: 0.5rem !important;
  padding-top: 0.5rem;
  border-top: 1px dashed #e2e8f0;
  font-size: 0.8rem;
  color: #718096;
}

.local-toast {
  background: #333;
  color: white;
  padding: 0.5rem 0.9rem;
  border-radius: 0.4rem;
  font-size: 0.8rem;
  opacity: 0.9;
  z-index: 999;
  height: 1rem;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  animation: fadeInOut 2.2s ease forwards;
  margin-top: 1rem;
}

.badge {
  display: inline-block;
  padding: 0.15rem 0.45rem;
  margin-left: 0.3rem;
  border-radius: 0.35rem;
  font-size: 0.65rem;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  opacity: 0.85;
  border: 1px solid transparent;
}

/* Significant (p < 0.05) */
.badge-significant {
  background: #e6f7e6;
  color: #2d7a2d;
  border-color: #8cd98c;
}

/* Not significant (optional) */
.badge-not-significant {
  background: #f5f5f5;
  color: #666;
  border-color: #ddd;
}

/* Low sample (n < 30) */
.badge-low-sample {
  background: #fff7e6;
  color: #8a6d1a;
  border-color: #f0d48a;
}

/* Very low sample (n < 10) */
.badge-very-low-sample {
  background: #fdeaea;
  color: #a33a3a;
  border-color: #f5a3a3;
}

/* High confidence (CI width < threshold) */
.badge-high-confidence {
  background: #e6f0ff;
  color: #2d4a7a;
  border-color: #8ca8d9;
}

/* Hover effect */
.badge:hover {
  opacity: 1;
}

.ci-cell {
  font-size: 0.75rem;
  font-weight: 600;
  color: #333;
  white-space: nowrap;
}

.ci-range {
  padding: 0.15rem 0.35rem;
  border-radius: 0.35rem;
  background: #f7f9fc;
  border: 1px solid #e2e6f0;
  display: inline-block;
}

.ci-low {
  color: #2d4a7a;
}

.ci-high {
  color: #2d4a7a;
}

.ci-separator {
  color: #999;
  padding: 0 0.15rem;
}

.segmentation-nudge {
  margin-top: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.nudge-icon {
  cursor: pointer;
  font-size: 1.2rem;
  padding: 0.35rem 0.55rem;
  border-radius: 0.4rem;
  background: #ffe5e5;
  color: #a33a3a;
  border: 1px solid #f5a3a3;
  transition: all 0.25s ease;
  user-select: none;
}

.nudge-icon:hover {
  background: #ffd6d6;
}

.nudge-icon.acknowledged {
  background: #e6f7e6;
  color: #2d7a2d;
  border-color: #8cd98c;
}

.nudge-panel {
  margin-left: 1rem;
  background: #fafafa;
  border: 1px solid #e5e5e5;
  padding: 1rem;
  border-radius: 0.5rem;
  white-space: wrap;
  width: 80%;
}

.nudge-text {
  font-size: 0.85rem;
  color: #444;
  margin-bottom: 0.75rem;
}

.nudge-check {
  font-size: 0.85rem;
  margin-left: 2rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@keyframes fadeInOut {
  0% {
    opacity: 0;
    transform: translateY(-4px);
  }
  10% {
    opacity: 0.9;
    transform: translateY(0);
  }
  90% {
    opacity: 0.9;
  }
  100% {
    opacity: 0;
  }
}
</style>
